import { useRef, useEffect } from "react";
import { useChatIsStreaming } from "../ChatContext";

/**
 * Fires a callback when the chat's stream transitions from active → idle.
 *
 * Works inside any component under `Chat.Root`. Consumers subscribe to
 * "my chat just finished streaming" without knowing anything about the
 * underlying store.
 *
 * @example
 * ```tsx
 * // Auto-focus an input when streaming ends
 * useOnStreamEnd(() => inputRef.current?.focus());
 *
 * // Play a sound
 * useOnStreamEnd(() => new Audio('/done.mp3').play());
 * ```
 */
export function useOnStreamEnd(callback: () => void) {
  const isStreaming = useChatIsStreaming();
  const wasStreaming = useRef(false);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (wasStreaming.current && !isStreaming) {
      callbackRef.current();
    }
    wasStreaming.current = isStreaming;
  }, [isStreaming]);
}
